import React from "react";
import {DashboardButton} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import {DashboardCard} from "@/ReactInit/Scripts/DashboardPage/components/DashboardCard";
import { formatNumberForDisplay } from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";

type EditableCategoryBlock = {
  id: string;
  category: string | null;
  products: string[];
  volumes: string[];
  lastYearVolumes: (number | null)[];
};

type Props = {
  year: number;
  blocks: EditableCategoryBlock[];
  reportComment: string;
  setReportComment: (text: string) => void;

  onPrev: () => void;
  onSubmit: () => void;

  submitLoading?: boolean;
};

export default function DashboardReportSummaryView(
  {
    year,
    blocks,
    reportComment,
    setReportComment,
    onPrev,
    onSubmit,
    submitLoading,
  }: Props) {
  const currentYear = year;
  const previousYear = year - 1;

  const rows = blocks.flatMap((block) =>
    block.products.map((product, idx) => ({
      key: `${block.id}-${idx}`,
      product,
      category: block.category,
      volume: block.volumes[idx] ?? "",
      lastYearVolume: block.lastYearVolumes[idx],
    }))
  );

  return (
    <div className="flex flex-col w-full mx-auto max-w-[932px]">
      <DashboardCard as="section" className="flex flex-col gap-5 tablet:gap-10">
        <div className="flex flex-col gap-3">
          <h2 className="text-5xl leading-[44px] font-semibold">
            Bekreftelse av rapporten
          </h2>
          <p className="text-xl leading-[30px] text-black-subtile">
            Sjekk at alt ser riktig ut før du sender inn rapporten.
          </p>
        </div>

        <div>
          <h3 className="mb-3 text-2xl font-semibold">{currentYear}-rapport</h3>

          {/* Mobile summary */}
          <div className="flex flex-col tablet:hidden">
            {rows.map((r) => (
              <div
                key={r.key}
                className="grid w-full grid-rows-4 py-2 border-b border-disabled-bg"
              >
                <div className="py-2 text-base leading-normal px-0.5">
                  <p className="inline-block w-1/2 mr-2 font-semibold text-black-subtile">
                    Produkt
                  </p>
                  {r.product || "—"}
                </div>

                <div className="py-2 text-base leading-normal px-0.5">
                  <p className="inline-block w-1/2 mr-2 font-semibold text-black-subtile">
                    Produktkategori
                  </p>
                  {r.category}
                </div>

                <div className="py-2 text-base font-semibold leading-normal px-0.5">
                  <p className="inline-block w-1/2 mr-2 font-semibold text-black-subtile">
                    Volum i {currentYear}
                  </p>
                  {formatNumberForDisplay(r.volume)}
                </div>

                <div className="py-2 text-base leading-normal px-0.5 text-black-subtile">
                  <p className="inline-block w-1/2 mr-2 font-semibold text-black-subtile">
                    Volum i {previousYear}
                  </p>
                  {formatNumberForDisplay(r.lastYearVolume)}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop summary */}
          <div className="hidden tablet:block">
            <div className="grid w-full grid-cols-4 border-b border-disabled-bg">
              <p className="py-4 text-base font-semibold leading-normal px-0.5 text-black-subtile tablet:px-4">
                Produkt
              </p>
              <p className="py-4 text-base font-semibold leading-normal px-0.5 text-black-subtile tablet:px-4">
                Produktkategori
              </p>
              <p className="py-4 text-base font-semibold leading-normal px-0.5 text-black-subtile tablet:px-4">
                Volum i {currentYear}
              </p>
              <p className="py-4 text-base font-semibold leading-normal px-0.5 text-black-subtile tablet:px-4">
                Volum i {previousYear}
              </p>
            </div>

            {rows.map((r) => (
              <div
                key={r.key}
                className="grid w-full grid-cols-4 border-b border-disabled-bg"
              >
                <div className="py-4 text-base leading-normal px-0.5 tablet:px-4">
                  {r.product || "—"}
                </div>
                <div className="py-4 text-base leading-normal px-0.5 tablet:px-4">
                  {r.category}
                </div>
                <div className="py-4 text-base font-semibold leading-normal px-0.5 tablet:px-4">
                  {formatNumberForDisplay(r.volume)}
                </div>
                <div className="py-4 text-base leading-normal px-0.5 text-black-subtile tablet:px-4">
                  {formatNumberForDisplay(r.lastYearVolume)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label
            htmlFor="comment"
            className="text-xl font-semibold leading-8 tablet:text-2xl"
          >
            Kommentar
          </label>

          <div className="flex w-full h-24 gap-3 p-3 bg-white border-2 border-brown-primary rounded-xl tablet:h-36 tablet:px-6">
            <div className="pointer-events-none icon-a-arrow-down !text-3xl !leading-none h-fit text-black-subtile" />
            <textarea
              id="comment"
              value={reportComment}
              onChange={(e) => setReportComment(e.target.value)}
              className="w-full text-xl leading-normal resize-none placeholder:text-base tablet:placeholder:text-xl"
              placeholder="Skriv inn kommentar (valgfritt)"
            />

            <button
              type="button"
              title="Fjern kommentar"
              onClick={() => setReportComment("")}
              className="h-fit"
            >
              <span className="sr-only">Fjern kommentar</span>
              <div
                className="icon-x !text-3xl !leading-none text-black-subtile"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-3 tablet:flex-row">
          <DashboardButton
            label="Gå tilbake"
            variant="underline"
            onClick={onPrev}
            icon="icon-arrow-left"
          />

          <DashboardButton
            label="Fullfør rapportering"
            variant="darkplain"
            onClick={onSubmit}
            icon="icon-check"
            loading={!!submitLoading}
            loadingText="Sender…"
          />
        </div>
      </DashboardCard>
    </div>
  );
}
